import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React, { useState } from "react";
import { SupportMessage } from "../types";
import SupportMediaModal from "./SupportMediaModal";

interface SupportMessageItemProps {
  message: SupportMessage;
  onDelete?: (messageId: string) => Promise<void>;
}

const SupportMessageItem: React.FC<SupportMessageItemProps> = ({
  message,
  onDelete,
}) => {
  const { user } = useProfile();
  const isOwnMessage = message.senderId === user?.id;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    type: "image" | "video" | "gif";
  } | null>(null);

  // For non-admin messages, show the GCCA Support avatar
  const isAdminMessage = message.senderRole === 0;

  const handleDelete = async () => {
    if (onDelete) {
      try {
        setIsDeleting(true);
        await onDelete(message.id);
      } catch (error) {
        console.error("Error deleting message:", error);
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirmation(false);
      }
    }
  };

  return (
    <>
      <div
        className={`flex items-start mb-4 ${
          isOwnMessage ? "justify-end" : "justify-start"
        } last:mb-0 group`}
      >
        {/* Avatar for support messages */}
        {!isOwnMessage && isAdminMessage && (
          <div className="flex-shrink-0 mr-3">
            <img
              src="/assets/images/avatar.png"
              alt="GCCA Support"
              className="w-8 h-8 rounded-full"
            />
          </div>
        )}

        <div
          className={
            message.type === "text"
              ? "max-w-[75%]"
              : "max-w-[45%] md:max-w-[35%]"
          }
        >
          <div
            className={`relative p-3 rounded-lg ${
              isOwnMessage
                ? `${theme.bg.brand} ${theme.text.white} rounded-br-none`
                : "bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-bl-none"
            }`}
          >
            {message.type === "deleted" && (
              <p className="italic">Message deleted</p>
            )}

            {message.type === "image" && (
              <div
                className="mt-2 rounded-lg overflow-hidden cursor-pointer"
                onClick={() => {
                  setPreviewMedia({ url: message.mediaUrl, type: "image" });
                  setShowMediaModal(true);
                }}
              >
                <img
                  src={message.mediaUrl}
                  alt="Image"
                  className="max-w-full"
                  onLoad={() => window.dispatchEvent(new Event("resize"))}
                />
              </div>
            )}

            {message.type === "gif" && (
              <div
                className="mt-2 rounded-lg overflow-hidden cursor-pointer"
                onClick={() => {
                  setPreviewMedia({ url: message.mediaUrl, type: "gif" });
                  setShowMediaModal(true);
                }}
              >
                <img
                  src={message.mediaUrl}
                  alt="GIF"
                  className="max-w-full"
                  onLoad={() => window.dispatchEvent(new Event("resize"))}
                />
              </div>
            )}

            {message.type === "video" && (
              <div className="mt-2 rounded-lg overflow-hidden">
                <video
                  controls
                  src={message.mediaUrl}
                  className="max-w-full"
                  onLoadedMetadata={() =>
                    window.dispatchEvent(new Event("resize"))
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewMedia({ url: message.mediaUrl, type: "video" });
                    setShowMediaModal(true);
                  }}
                />
              </div>
            )}

            {message.type === "file" && (
              <div className="flex items-center">
                <svg
                  className="w-8 h-8 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
                </svg>
                <a
                  href={message.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-violet-400 transition-colors"
                >
                  {message.content || "Download file"}
                </a>
              </div>
            )}

            {(message.type === "text" ||
              (message.content && message.type !== "file")) && (
              <p className={`${message.type === "text" ? "" : "mt-2"}`}>
                {message.content}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className={`text-xs ${theme.text.muted}`}>
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
            {isOwnMessage &&
              (message.read ? (
                <svg
                  className={`w-5 h-3 shrink-0 fill-current ${theme.text.success}`}
                  viewBox="0 0 20 12"
                >
                  <path d="M10.402 6.988l1.586 1.586L18.28 2.28a1 1 0 011.414 1.414l-7 7a1 1 0 01-1.414 0L8.988 8.402l-2.293 2.293a1 1 0 01-1.414 0l-3-3A1 1 0 013.695 6.28l2.293 2.293L12.28 2.28a1 1 0 011.414 1.414l-3.293 3.293z" />
                </svg>
              ) : (
                <svg
                  className="w-3 h-3 shrink-0 fill-current text-gray-400"
                  viewBox="0 0 12 12"
                >
                  <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                </svg>
              ))}
          </div>
        </div>
      </div>

      {/* Media Preview Modal */}
      {showMediaModal && previewMedia && (
        <SupportMediaModal
          isOpen={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          mediaUrl={previewMedia.url}
          mediaType={previewMedia.type}
        />
      )}
    </>
  );
};

export default SupportMessageItem;
